import { useEffect } from 'react';
import { useMotionValue } from 'framer-motion';

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/**
 * Scroll progress driven by plain scroll events instead of the animation
 * library's scroll pipeline (which proved unreliable — see ConsoleShowcase).
 *
 * Semantics match useScroll's ['start end', 'end start']:
 *   0 → element's top touches the viewport bottom
 *   1 → element's bottom leaves the viewport top
 * Returns a MotionValue, so existing useTransform/useSpring chains keep working.
 *
 * Performance: the element is measured only on mount, resize, and a couple of
 * settle points after load (images shifting layout). During scroll the value
 * is pure math on scrollY — zero layout reads — and updates are coalesced to
 * one per frame. With 15+ instances on a page this is the difference between
 * smooth and janky on phones.
 */
export const useViewportProgress = (ref) => {
  const progress = useMotionValue(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    let docTop = 0;
    let height = 1;
    let ticking = false;

    const apply = () => {
      const vh = window.innerHeight || 1;
      const top = docTop - window.scrollY; // viewport-relative, no layout read
      progress.set(clamp((vh - top) / (vh + height), 0, 1));
    };

    const measure = () => {
      const r = el.getBoundingClientRect();
      docTop = r.top + window.scrollY;
      height = r.height || 1;
      apply();
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        apply();
      });
    };

    measure();
    // Late layout shifts (fonts, lazy images) move docTop — re-measure as the page settles
    const settle1 = setTimeout(measure, 900);
    const settle2 = setTimeout(measure, 2800);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measure, { passive: true });
    window.addEventListener('load', measure, { once: true });
    return () => {
      clearTimeout(settle1);
      clearTimeout(settle2);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', measure);
      window.removeEventListener('load', measure);
    };
  }, [ref, progress]);

  return progress;
};

/** Whole-page progress (0 at top, 1 at bottom) on the same plain-JS engine. */
export const usePageProgress = () => {
  const progress = useMotionValue(0);

  useEffect(() => {
    let max = 1;
    let ticking = false;

    const apply = () => {
      progress.set(max > 0 ? clamp(window.scrollY / max, 0, 1) : 0);
    };
    const measure = () => {
      max = document.documentElement.scrollHeight - window.innerHeight;
      apply();
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        apply();
      });
    };

    measure();
    const settle = setTimeout(measure, 1200);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measure, { passive: true });
    window.addEventListener('load', measure, { once: true });
    return () => {
      clearTimeout(settle);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', measure);
      window.removeEventListener('load', measure);
    };
  }, [progress]);

  return progress;
};
